import { faArrowUpRightFromSquare, faFileLines } from '@fortawesome/pro-solid-svg-icons';
import { Typography } from '@mui/material';

import DocumentLink from '../../components/DocumentLink/DocumentLink';
import Icon from '../../components/Icon/Icon';
import Link from '../../components/Link/Link';
import ListField from '../../components/ListField/ListField';
import Subtitle from '../../components/Subtitle/Subtitle';
import Summary from '../../components/Summary/Summary';
import { formatNumberByThousands } from '../../helpers/formatNumberByThousands';
import { useAppSelector } from '../../hooks';
import useGetApplicationSummary from '../../hooks/useGetApplicationSummaryService';
import useTranslation from '../../hooks/useTranslation';
import ConsolidationsSummaryRow from './ConsolidationsSummaryRow';
import FieldSummaryRow from './FieldSummaryRow';

const CoApplicantSubmitPageSidePanel = () => {
    const translate = useTranslation();
    const consolidations = useAppSelector((state) => state.form.consolidations);
    const internalLoans = useAppSelector((state) => state.form.internalLoans);
    const applicationSummary = useGetApplicationSummary();

    return (
        <>
            <Subtitle text={translate('page.submit.subtitle.details')} />
            <Summary label={translate('field.section.about-you')} expand={false}>
                <FieldSummaryRow
                    name="coapplicantMaritalStatus"
                    label={translate('page.submit.applicant-marital-status.label')}
                />
                <FieldSummaryRow
                    name="coapplicantHabitationForm"
                    label={translate('page.submit.applicant-habitation-form.label')}
                />
                <FieldSummaryRow
                    name="coapplicantEmploymentType"
                    label={translate('page.submit.applicant-employment-type.label')}
                />
                <FieldSummaryRow
                    name="coapplicantYearlyGrossIncome"
                    label={translate('page.submit.applicant-yearly-gross-income.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="coapplicantMonthlyIncomeOther"
                    label={translate('page.submit.applicant-monthly-income-other.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="coapplicantMortgageDebt"
                    label={translate('page.submit.applicant-mortage-debt.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="coapplicantMortgageMonthlyCost"
                    label={translate('page.submit.applicant-mortage-monthly-cost.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="coapplicantCarLoanSum"
                    label={translate('page.submit.applicant-car-loan-sum.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="coapplicantCarLoanMonthlyCost"
                    label={translate('page.submit.applicant-car-loan-monthly-cost.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="coapplicantCarLeasingMonthlyCost"
                    label={translate('page.submit.applicant-car-leasing-monthly-cost.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="coapplicantStudentLoanSum"
                    label={translate('page.submit.applicant-student-loan-sum.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="coapplicantStudentLoanMonthlyCost"
                    label={translate('page.submit.applicant-student-loan-monthly-cost.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
            </Summary>
            {(applicationSummary?.selectedConsolidations?.length ?? 0) > 0 && (
                <Summary label={translate('field.section.consolidation-main-applicant.label')} expand={false}>
                    <ConsolidationsSummaryRow consolidations={applicationSummary?.selectedConsolidations ?? []} />
                </Summary>
            )}
            {(consolidations?.length > 0 || internalLoans?.length > 0) && (
                <Summary label={translate('field.section.consolidation-co-applicant.label')} expand={false}>
                    <ConsolidationsSummaryRow consolidations={[...consolidations, ...internalLoans]} />
                </Summary>
            )}

            <Subtitle text={translate('page.submit.subtitle.documents')} />
            <ListField
                name="commonTermsAndSekki"
                items={[
                    {
                        index: '1',
                        icon: <Icon icon={faFileLines} />,
                        label: (
                            <Link onClick={() => window.open(translate('page.submit.link.common-terms'), '_blank')}>
                                <Typography variant="body2">{translate('page.submit.label.common-terms')}</Typography>
                            </Link>
                        ),
                        endIcon: <Icon icon={faArrowUpRightFromSquare} size="sm" />,
                    },
                    {
                        index: '2',
                        icon: <Icon icon={faFileLines} />,
                        label: (
                            <Link onClick={() => window.open(translate('page.submit.link.sekki'), '_blank')}>
                                <Typography variant="body2">{translate('page.submit.label.sekki')}</Typography>
                            </Link>
                        ),
                        endIcon: <Icon icon={faArrowUpRightFromSquare} size="sm" />,
                    },
                ]}
            />
            <Subtitle text={translate('page.submit.subtitle.gdpr')} />
            <Typography variant="body2"> {translate('page.submit.body-text.gdpr')}</Typography>
            <Typography variant="body2">
                <DocumentLink href={translate('page.submit.link.gdpr')} text={translate('page.submit.label.gdpr')} />
            </Typography>
        </>
    );
};

export default CoApplicantSubmitPageSidePanel;
